import React from 'react';
import { Typography } from '@mui/material';

const EC_ORDER = [
  { key: 'ec2', year: '1992' },
  { key: 'ec3', year: '1993' },
  { key: 'ec5', year: '1995' },
];

/**
 * Heading from which EN parts are actually present in the API payload (same keys as result panels).
 */
export function eurocodeDesignChecksTitle(designChecks) {
  if (!designChecks) return 'Design checks (Eurocode)';
  const years = EC_ORDER.filter(({ key }) => designChecks[key]).map((o) => o.year);
  if (years.length === 0) return 'Design checks (Eurocode)';
  if (years.length === 1) return `EN ${years[0]} design checks`;
  if (years.length === 2) return `EN ${years[0]} & ${years[1]} design checks`;
  return `EN ${years[0]}, ${years[1]} & ${years[2]} design checks`;
}

/**
 * Plain-text NA factors for print/PDF (no HTML tags).
 */
export function eurocodeNaNotePlain(designChecks, naFi) {
  if (!naFi || !designChecks) return '';
  const showEc2 = Boolean(designChecks.ec2);
  const showEc3 = Boolean(designChecks.ec3);
  const showEc5 = Boolean(designChecks.ec5);
  if (!showEc2 && !showEc3 && !showEc5) return '';

  const parts = [];
  if (naFi.country) {
    parts.push(`${naFi.country} national annex (informative).`);
  }
  const factors = [];
  if (showEc2 && naFi.concrete_en1992) {
    const c = naFi.concrete_en1992;
    factors.push(
      `α_cc = ${c.alpha_cc ?? '—'}, γ_c = ${c.gamma_c ?? '—'}, reinforcing γ_s = ${c.gamma_s_reinforcement ?? '—'}`,
    );
  }
  if (showEc3 && naFi.steel_en1993) {
    const s = naFi.steel_en1993;
    factors.push(`Steel γ_M0/γ_M1 = ${s.gamma_M0 ?? '—'}/${s.gamma_M1 ?? '—'}`);
  }
  if (showEc5 && naFi.timber_en1995?.gamma_M) {
    factors.push(`timber (EC5 Table 2.3 style): ${naFi.timber_en1995.gamma_M}`);
  }
  if (factors.length) {
    parts.push(`${factors.join('. ')}.`);
  }
  parts.push('See API for full NA notes.');
  return parts.join(' ');
}

export function EurocodeDesignChecksSubtitle({ designChecks, naFi }) {
  if (!naFi || !designChecks) return null;
  const showEc2 = Boolean(designChecks.ec2);
  const showEc3 = Boolean(designChecks.ec3);
  const showEc5 = Boolean(designChecks.ec5);
  if (!showEc2 && !showEc3 && !showEc5) return null;

  return (
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }} component="div">
      {naFi.country && <>{naFi.country} national annex (informative). </>}
      {showEc2 && naFi.concrete_en1992 && (
        <>
          α<sub>cc</sub> = {naFi.concrete_en1992.alpha_cc ?? '—'}, γ<sub>c</sub> ={' '}
          {naFi.concrete_en1992.gamma_c ?? '—'}, reinforcing γ<sub>s</sub> ={' '}
          {naFi.concrete_en1992.gamma_s_reinforcement ?? '—'}
          .{' '}
        </>
      )}
      {showEc3 && naFi.steel_en1993 && (
        <>
          Steel γ<sub>M0</sub>/γ<sub>M1</sub> = {naFi.steel_en1993.gamma_M0 ?? '—'}/
          {naFi.steel_en1993.gamma_M1 ?? '—'}
          .{' '}
        </>
      )}
      {showEc5 && naFi.timber_en1995?.gamma_M && (
        <>
          Timber (EC5 Table 2.3 style): {naFi.timber_en1995.gamma_M}.{' '}
        </>
      )}
      See API for full NA notes.
    </Typography>
  );
}
